import '../../../invoicing/shared/style.css';

import React from 'react';
import { FormApi } from 'final-form';
import { Modal, Button, ModalDrawerControllerProps, ModalCloseReason, Group } from '@economic/gaudi';

import { Field } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import { InvoicingMessage } from '../../../../../../types/internal-api';
import { useEmailOptionsPdfSelected } from '../../../invoicing/invoices/utils';
import { InvoicingMessageType, SendByType } from '../../../invoicing/invoices/book-and-send/types';
import { SendBulkCustomerLedgerCardsDialogFormData } from '../types';
import { SendAsEmail } from '../../../invoicing/shared/components/SendBy';
import { EmailOptionsDrawerToggle } from '../../../invoicing/shared/components/EmailOptionsDrawerToggle';
import { DisplayMessages } from '../../../invoicing/shared/components/DisplayMessages';
import { EmailTemplatePicker } from '../../../invoicing/invoices/utils/EmailTemplatePicker';
import { emailTemplateUpdater } from '../../../invoicing/invoices/utils/emailTemplateUpdater';
import { SalesModalFormWithEmailOptionsDrawer } from '../../../invoicing/shared/components/SalesModalFormWithEmailOptionsDrawer';

function useRequiredFieldsValidation(): InvoicingMessage[] {
    const requiredFieldsMessages = [] as InvoicingMessage[];
    // check if email options make sense for sending order.
    const isPdfSelected = useEmailOptionsPdfSelected();

    if (!isPdfSelected) {
        requiredFieldsMessages.push({ message: t`FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR`, type: InvoicingMessageType.Error });
    }

    return requiredFieldsMessages;
}

function SendBulkCustomerLedgerCardsDialogContent({
    messages,
    information,
    drawer,
    formAction: onSendAction,
}: {
    drawer: ModalDrawerControllerProps;
    messages: InvoicingMessage[];
    information: SendBulkCustomerLedgerCardsDialogFormData;
    formAction: (sendByType: SendByType) => void;
}) {
    const requiredFieldsValidationMessages = useRequiredFieldsValidation();
    messages = [...messages, ...requiredFieldsValidationMessages];

    return (
        <>
            <Modal.Header>
                {t('FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_HEADER', {
                    COUNT: `${information.validCustomerIds.length}`,
                })}
            </Modal.Header>

            <Modal.Content>
                <SendAsEmail title={t`FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_SINGLE_TEXT`} />

                <EmailOptionsDrawerToggle
                    drawerVisible={drawer.visible}
                    onShowDrawer={drawer.show}
                    onToggleDrawer={drawer.toggle}
                />

                <EmailTemplatePicker templates={information.templates} />

                <div className="book-and-send-dialog__messages">
                    <DisplayMessages messages={messages} />
                </div>

                <div className="book-and-send-dialog__options">
                    <Field.Checkbox
                        name="emailOptions.showPdf"
                        text={t('FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_SHOWAFTERSENDING', {
                            COUNT: `${information.validCustomerIds.length}`,
                        })}
                    />
                </div>
                <Field.Hidden name="sendByType" value={Number(SendByType.undefined)} />
            </Modal.Content>

            <Modal.Footer>
                <Group>
                    <Button
                        className="book-and-send-dialog__action-button"
                        appearance="primary"
                        type="submit"
                        onClick={() => onSendAction(SendByType.email)}
                        disabled={!!requiredFieldsValidationMessages.length || information.validCustomerIds.length === 0}>
                        {t`FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_SEND`}
                    </Button>
                </Group>
            </Modal.Footer>
        </>
    );
}

interface SendBulkCustomerLedgerCardsDialogProps {
    disclosure: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
    loading: boolean;
    messages: InvoicingMessage[];
    information?: SendBulkCustomerLedgerCardsDialogFormData;
    onSend: (values: SendBulkCustomerLedgerCardsDialogFormData) => Promise<void>;
    onHide: (reason: ModalCloseReason) => void;
    error: boolean;
}

export function SendBulkCustomerLedgerCardsDialog({
    disclosure,
    loading,
    messages,
    information,
    onSend,
    onHide,
    error,
}: SendBulkCustomerLedgerCardsDialogProps) {
    function onFormAction(form: FormApi<any>, sendByType: SendByType) {
        form.change('sendByType', Number(sendByType));
        form.blur('sendByType');
        form.submit();
    }

    function updater() {
        return [emailTemplateUpdater()];
    }

    let errorMessages: InvoicingMessage[];
    if (error) {
        errorMessages = [{ type: InvoicingMessageType.Error, message: t`FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR` }];
    } else {
        errorMessages = messages.filter(m => m.type === InvoicingMessageType.Error) ?? [];
    }

    return (
        <SalesModalFormWithEmailOptionsDrawer
            loading={loading}
            errorTitle={t`FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_ERRORS_HEADER_CANNOTSEND`}
            errorMessages={errorMessages}
            disclosure={disclosure}
            onHide={onHide}
            className="book-and-send-dialog"
            initialData={{ ...information }}
            onSubmit={onSend}
            updates={updater}
            hideRecipientsSection>
            {(form, drawer) => (
                <SendBulkCustomerLedgerCardsDialogContent
                    formAction={sendByType => onFormAction(form, sendByType)}
                    messages={messages}
                    information={information!}
                    drawer={drawer}
                />
            )}
        </SalesModalFormWithEmailOptionsDrawer>
    );
}
